import { VoucherCategory } from '../../vouchers/index.js';
import { isCalculatedInterest } from '../../vouchers/setup.js';
export const COST_CEILING_DISABLED = {
    enabled: false,
    ceilingAmount: null,
    voucherTypesCeiling: [],
};
export const getCostCeiling = (singleCase, voucherTypes) => {
    if (singleCase.costCeilingEnabled !== true) {
        return COST_CEILING_DISABLED;
    }
    return {
        enabled: true,
        ceilingAmount: singleCase.costCeilingAmount ?? null,
        voucherTypesCeiling: voucherTypes
            .filter((t) => t.countsTowardCostCeiling)
            .map((t) => t.voucherTypeId)
            .sort(),
    };
};
export const isDynamicCeilingVoucher = (voucher, voucherTypesCeiling) => isCalculatedInterest(voucher) &&
    voucherTypesCeiling.includes(voucher.voucherTypeId);
const ceilingBasis = (vouchers, ceilingAmount) => {
    if (ceilingAmount !== null) {
        return Math.abs(ceilingAmount);
    }
    let principal = 0;
    for (const voucher of vouchers) {
        if (voucher.category === VoucherCategory.PRINCIPAL) {
            principal +=
                voucher.amount >= 0
                    ? voucher.amount - (voucher.distributionAmount ?? 0)
                    : voucher.amount;
        }
    }
    // Credits beyond the principal leave no ceiling to fill, not a positive one.
    return Math.max(0, -principal);
};
const coveredStaticCosts = (vouchers, voucherTypesCeiling) => {
    let staticCosts = 0;
    for (const voucher of vouchers) {
        if (voucher.amount < 0 &&
            voucherTypesCeiling.includes(voucher.voucherTypeId) &&
            !isDynamicCeilingVoucher(voucher, voucherTypesCeiling)) {
            staticCosts += Math.abs(voucher.amount);
        }
    }
    return staticCosts;
};
export const getCostCeilingSummary = (vouchers, costCeiling) => {
    if (!costCeiling.enabled) {
        return null;
    }
    const ceiling = ceilingBasis(vouchers, costCeiling.ceilingAmount);
    const staticCosts = coveredStaticCosts(vouchers, costCeiling.voucherTypesCeiling);
    return {
        ceiling,
        staticCosts,
        dynamicBudget: Math.max(0, ceiling - staticCosts),
        exceededByStaticCosts: ceiling !== 0 && staticCosts > ceiling,
    };
};
const trimVoucherAmount = (voucher, excess) => {
    voucher.amount += excess;
    voucher.costCeilingApplied = true;
};
const trimVoucherHistory = (voucher) => {
    const history = voucher.interestHistory;
    if (history == null) {
        return;
    }
    const historyAmount = history.reduce((sum, entry) => sum + entry.amount, 0);
    let left = voucher.amount - historyAmount;
    for (let i = history.length - 1; i >= 0 && left > 0; i--) {
        if (history[i].amount >= 0) {
            continue;
        }
        const take = Math.min(left, -history[i].amount);
        if (take > 0) {
            history[i] = { ...history[i], amount: history[i].amount + take };
            left -= take;
        }
    }
};
export const createCostCeilingLedger = (inputVouchers, costCeiling) => {
    if (!costCeiling.enabled) {
        return null;
    }
    const { ceilingAmount, voucherTypesCeiling } = costCeiling;
    const ceiling = ceilingBasis(inputVouchers, ceilingAmount);
    const maximumBudget = Math.max(0, ceiling - coveredStaticCosts(inputVouchers, voucherTypesCeiling));
    let remaining = maximumBudget;
    const pending = new Map();
    const suppressed = new Map();
    const trimmedVouchers = new Set();
    const record = (voucher, amount) => {
        if (!isDynamicCeilingVoucher(voucher, voucherTypesCeiling) ||
            amount === 0) {
            return;
        }
        if (amount < 0) {
            pending.set(voucher, (pending.get(voucher) ?? 0) - amount);
            return;
        }
        let correction = amount;
        const pendingAmount = pending.get(voucher) ?? 0;
        if (pendingAmount > 0) {
            const pendingCorrection = Math.min(correction, pendingAmount);
            const correctedPendingAmount = pendingAmount - pendingCorrection;
            if (correctedPendingAmount === 0) {
                pending.delete(voucher);
            }
            else {
                pending.set(voucher, correctedPendingAmount);
            }
            correction -= pendingCorrection;
        }
        const suppressedAmount = suppressed.get(voucher) ?? 0;
        const suppressedCorrection = Math.min(correction, suppressedAmount);
        if (suppressedCorrection > 0) {
            voucher.amount -= suppressedCorrection;
            const correctedSuppressedAmount = suppressedAmount - suppressedCorrection;
            if (correctedSuppressedAmount === 0) {
                suppressed.delete(voucher);
            }
            else {
                suppressed.set(voucher, correctedSuppressedAmount);
            }
            correction -= suppressedCorrection;
        }
        remaining = Math.min(maximumBudget, remaining + correction);
    };
    const settle = () => {
        if (pending.size === 0) {
            return;
        }
        const entries = [...pending];
        pending.clear();
        const total = entries.reduce((sum, [, amount]) => sum + amount, 0);
        const budget = Math.min(total, remaining);
        let allocated = 0;
        entries.forEach(([voucher, amount], index) => {
            const allowed = index === entries.length - 1
                ? Math.min(amount, budget - allocated)
                : Math.min(amount, Math.floor((amount * budget) / total));
            allocated += allowed;
            const excess = amount - allowed;
            if (excess > 0) {
                trimVoucherAmount(voucher, excess);
                trimmedVouchers.add(voucher);
                suppressed.set(voucher, (suppressed.get(voucher) ?? 0) + excess);
            }
        });
        remaining = Math.max(0, remaining - allocated);
    };
    const finalize = () => {
        for (const voucher of trimmedVouchers) {
            const suppressedAmount = suppressed.get(voucher) ?? 0;
            if (suppressedAmount > 0) {
                trimVoucherHistory(voucher);
            }
            else {
                delete voucher.costCeilingApplied;
            }
        }
    };
    return { record, settle, finalize };
};
//# sourceMappingURL=cost-ceiling.js.map